import React, { useState } from 'react';
import { useMalcolmContext } from '../../contexts/MalcolmContext';
import FeedItem from './FeedItem';

const IntelligenceFeedMain = () => {
  const { feedItems, loading, error, activeFilter, searchQuery } = useMalcolmContext();
  const [selectedItem, setSelectedItem] = useState(null);

  const filteredItems = feedItems.filter(item => {
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = item.title.toLowerCase().includes(query);
      const matchesSummary = item.summary?.toLowerCase().includes(query);
      const matchesCompany = item.source?.company?.toLowerCase().includes(query);
      if (!matchesTitle && !matchesSummary && !matchesCompany) {
        return false;
      }
    }

    // Apply active filter
    switch (activeFilter) {
      case 'high_relevance':
        return item.relevance_score >= 80;
      case 'trending':
        return item.trending_score >= 70;
      case 'beta':
        return item.tags?.includes('beta_access');
      default:
        return true;
    }
  });

  if (loading && feedItems.length === 0) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-stone-200 p-6 animate-pulse">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-stone-200 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-stone-200 rounded w-3/4"></div>
                  <div className="h-3 bg-stone-200 rounded w-1/2"></div>
                  <div className="h-3 bg-stone-200 rounded w-full"></div>
                  <div className="h-3 bg-stone-200 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading feed</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (filteredItems.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">No items found</h3>
          <p className="text-stone-600 mb-4">
            {searchQuery 
              ? `No results for "${searchQuery}" in ${activeFilter.replace('_', ' ')} filter`
              : `No items match the current ${activeFilter.replace('_', ' ')} filter`
            }
          </p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Clear search
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Feed items */}
      <div className="space-y-4">
        {filteredItems.map((item) => (
          <FeedItem
            key={item.id}
            item={item}
            onSelect={() => setSelectedItem(item)}
            isSelected={selectedItem?.id === item.id}
          />
        ))}
      </div>

      {/* Load more indicator */}
      {filteredItems.length >= 50 && (
        <div className="mt-8 text-center">
          <button className="bg-stone-100 hover:bg-stone-200 text-stone-700 px-6 py-3 rounded-lg font-medium transition-colors">
            Load More Articles
          </button>
        </div>
      )}

      {/* Summary stats */}
      <div className="mt-8 p-4 bg-stone-50 rounded-lg">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-slate-900">{filteredItems.length}</div>
            <div className="text-sm text-stone-600">Items Shown</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-indigo-600">
              {filteredItems.filter(item => item.relevance_score >= 80).length}
            </div>
            <div className="text-sm text-stone-600">High Relevance</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-pink-600">
              {filteredItems.filter(item => item.trending_score >= 70).length}
            </div>
            <div className="text-sm text-stone-600">Trending</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-violet-600">
              {filteredItems.filter(item => item.tags?.includes('beta_access')).length}
            </div>
            <div className="text-sm text-stone-600">Beta Access</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntelligenceFeedMain;